import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { merchantApplicationsApi } from '../api/merchantApplicationsApi';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const KEY = 'merchant-applications';

export const useMerchantApplications = () =>
  useQuery({
    queryKey: [KEY],
    queryFn: () => merchantApplicationsApi.listPending(),
    staleTime: 30 * 1000,
  });

export const useMerchantApplicationDetail = (id: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, id],
    queryFn: () => merchantApplicationsApi.getDetail(id as string),
    enabled: !!id,
  });

export const useApproveMerchantApplication = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => merchantApplicationsApi.approve(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success(res.message || 'Merchant approved and activated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to approve merchant')),
  });
};

export const useRejectMerchantApplication = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      merchantApplicationsApi.reject(id, reason),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success(res.message || 'Merchant application rejected');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to reject merchant application')),
  });
};
